import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, filter, tap } from 'rxjs/operators';


import { MenuApiService } from '../menu-api.service';
import { MenuStoreService } from '../menu-store.service';
import { LocationSetsActions } from '@emm/menu/store/location-sets/location-sets.actions';
import {SubmenusActions} from '@emm/menu/store/submenus/submenus.actions';
import {ConsumersActions} from '@emm/menu/store/consumer/consumers.actions';
import {ItemsActions} from '@emm/menu/store/items/items.actions';
import {NotificationService} from '@emm/shared/services/notification.service';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';

@Injectable({
    providedIn: 'root'
})
export class SubmenusEffects {

    locationSetSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LocationSetsActions.select),
            withLatestFrom(this.store.queryFeatureScope.selected$),
            filter(([_, featureScope]) => featureScope === FeatureScope.Global),
            switchMap(() => of(SubmenusActions.load()))
        )
    );

    consumerSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ConsumersActions.select),
            withLatestFrom(this.store.queryFeatureScope.selected$),
            filter(([_, featureScope]) => featureScope === FeatureScope.Client),
            switchMap(() => of(SubmenusActions.load()))
        )
    );

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.load),
            withLatestFrom(this.store.queryConsumers.menuParamsContext$, this.store.queryFeatureScope.selected$),
            switchMap(([_, { menuKey }, featureScope]) => {
                return this.api.getSubmenusByFeatureScope(featureScope, menuKey)
                    .pipe(
                        map((submenus) => SubmenusActions.loadSuccess({submenus})),
                        catchError(({error}) => of(SubmenusActions.loadFailure({error})))
                    );
                }
            )
        )
    );

    submenuSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.select),
            switchMap(() => of(ItemsActions.load()))
        )
    );

    saveAssignments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.saveAssignments),
            withLatestFrom(this.store.queryConsumers.menuParamsContext$),
            switchMap(([{ assignments }, { menuKey }]) => {
                    return this.api.saveConsumerMenuAssignments(assignments, menuKey).pipe(
                        map((response) => SubmenusActions.saveAssignmentsSuccess({response})),
                        catchError(({error}) => of(SubmenusActions.saveAssignmentsFailure({error})))
                    );
                }
            )
        )
    );


    saveAssignmentsSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.saveAssignmentsSuccess),
            tap(() => {
                this.notificationService.success('Saved successfully');
            }),
            switchMap(() => of(SubmenusActions.load()))
        )
    );



    add$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.add),
            withLatestFrom(this.store.queryConsumers.menuParamsContext$),
            switchMap(([{ submenu }, { effectiveDate, menuKey }]) =>
                this.api.addSubmenu(menuKey, submenu).pipe(
                    map((_) => SubmenusActions.addSuccess()),
                    catchError(({ error }) => {
                        this.notificationService.error(error.error.message); // consider how to do this inside addFailure action instead
                        return of(SubmenusActions.addFailure({error}));
                    })
                )
            )
        )
    );


    addSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.addSuccess),
            tap(() => {
                this.notificationService.success('Submenu added successfully');
            }),
            switchMap(() => of(SubmenusActions.load()))
        )
    );



    constructor(
        private actions$: Actions,
        private api: MenuApiService,
        private store: MenuStoreService,
        private notificationService: NotificationService,
    ) {}

}
